import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, createToast, Input, Modal, Radio, Text } from '@maui/elements'
import { registerModal } from '../AppModal'
import { CountryDropdown } from 'react-country-region-selector'
import useUpgradeSubscription from '../../store/upgrade/hooks'
import { useDispatch } from 'react-redux'
import { closeModal } from '../../store/appModal/actions'
import { STATE_REQUESTING, STATE_SUCCESS } from '../../store/upgrade/constants'

const ErrorMessage = ({ error }) => {
  if (!error) { return null }

  return (
    <Text variant="body2" mt="spaceGapSmall" color="colorActionDestructive">
      {error}
    </Text>
  )
}

const EODUpgradeModal = ({ ...modalProps }) => {
  const dispatch = useDispatch()
  const { state, error, upgradeSubscription } = useUpgradeSubscription()

  const [errorState, setErrorState] = useState({})
  // Can also be MA_EOD
  const [formState, setFormState] = useState({
    sku: 'ASK_AN_EXPERT',
    Mandiant_Advantage_Primary_Business__c: 'MA_EOD_MORE_UNITS',
    company_country: '',
    has_active_project: 'Yes',
    interest_reason: 'Internal Project',
    phone_no: '',
    preferred_contact_method: 'Email'
  })
  const updateFormField = useCallback((name, value) => {
    // Merge in the new value
    setFormState({ ...formState, [name]: value })

    // If there are no errors or there are no errors for this field, return
    if (!errorState.errors?.[name]) { return }

    // Otherwise destructure out the errors for this field
    const { [name]: toRemove, ...newErrors } = errorState.errors
    setErrorState({ ...errorState, errors: newErrors })
  }, [formState, errorState, setFormState, setErrorState])

  // On service error, update form error state & messaging
  useEffect(() => {
    if (error && error.status === 422) {
      createToast({
        variant: 'error',
        title: 'Error',
        message: 'Please correct the form before clicking submit',
        duration: 5000,
      })
      setErrorState({
        message: 'Please correct the form above before clicking submit',
        errors: error.body.errors
      })
    } else if (error) {
      createToast({
        variant: 'error',
        title: 'Error',
        message: 'An unexpected error occurred',
        duration: 5000,
      })
      setErrorState({
        message: error.ui ? error.message : 'An unexpected error has occurred'
      })
    } else {
      setErrorState({})
    }
  }, [error, setErrorState])

  // On state change to success, close the modal and scroll to screen top
  useEffect(() => {
    if (state === STATE_SUCCESS) {
      createToast({
        variant: 'success',
        title: 'Request sent',
        message: 'Your request was submitted successfully',
        duration: 5000,
      })
      dispatch(closeModal('EODUpgradeModal'))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [dispatch, state])

  const submitForm = useCallback(() => {
    upgradeSubscription(formState)
  }, [formState, upgradeSubscription])

  const requesting = state === STATE_REQUESTING
  const { errors = {} } = errorState
  return (
    <Modal
      {...modalProps}
      size="medium"
    >
      <Modal.Header onClose={modalProps.onClose} color='colorTextMain'>
        <Modal.TitleIcon name="add" />
        <Modal.Title>Request More Units</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box mb="spaceGapXxLarge">
          <CountryDropdown
            style={{
              background: 'var(--maui-c-input-background-color)',
              borderColor: 'var(--maui-c-input-border-color)',
              color: 'var(--maui-c-input-color)',
              paddingLeft: 'var(--maui-c-input-size-small-padding-left)',
              paddingRight: 'var(--maui-c-input-size-small-padding-left)',
              height: 'var(--maui-c-input-size-small-height)',
              borderRadius: 'var(--maui-radius-medium)',
            }}
            value={formState.company_country}
            onChange={value => {
              updateFormField('company_country', value)
            }}
            showDefaultOption={true}
            priorityOptions={['US']}
          />
          <Box mt="spaceGapXLarge">
            <ErrorMessage error={errors.company_country && 'This is a required field'} />
          </Box>
        </Box>
        <Box mb="spaceGapXLarge">
          <Text variant="body2" mb="spaceGapLarge">What is your preferred contact method?</Text>
          <Radio
            value='Email'
            name='Email'
            checked={formState.preferred_contact_method === 'Email'}
            onChange={e => {
              if (e.target.checked) {
                updateFormField('preferred_contact_method', 'Email')
              }
            }}
          >
            Email
          </Radio>
          <Radio
            value='Phone'
            name='Phone'
            checked={formState.preferred_contact_method === 'Phone'}
            onChange={e => {
              if (e.target.checked) {
                updateFormField('preferred_contact_method', 'Phone')
              }
            }}
          >
            Phone
          </Radio>
          <Input
            placeholder='Phone'
            maxLength='14'
            type='tel'
            disabled={formState.preferred_contact_method !== 'Phone'}
            value={formState.phone_no}
            onChange={e => {
              updateFormField('phone_no', e.target.value)
            }}
          />
          <ErrorMessage error={errors.phone_no?.[0]} />
        </Box>
        <Text>Select "Submit" to request more units and a member of our customer support team will be in touch soon</Text>
      </Modal.Body>
      <Modal.Footer>
        <Box gap="spaceGapMedium" display="flex">
          <Button variant="secondary" onClick={() => dispatch(closeModal('EODUpgradeModal'))}>Cancel</Button>
          <Button onClick={submitForm} disabled={requesting}>Submit</Button>
        </Box>
      </Modal.Footer>
    </Modal>
  )
}

EODUpgradeModal.propTypes = {
  modalProps: PropTypes.object,
}

registerModal('EODUpgradeModal', EODUpgradeModal)

export default EODUpgradeModal
